import React, {useState} from 'react';

interface ImageUploaderProps {
  images: (string | File)[];
  onChange: (newImages: (string | File)[]) => void;
  maxImages?: number;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  images,
  onChange,
  maxImages = 10,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const combined = [...images, ...newFiles].slice(0, maxImages);
      onChange(combined);
    }
  };

  const removeImage = (index: number) => {
    const updated = images.filter((_, i) => i !== index);
    onChange(updated);
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    color: '#aaa',
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
  };

  const uploadButtonStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '120px',
    borderRadius: '12px',
    border: `2px dashed ${isHovered ? '#daff00' : '#444'}`,
    backgroundColor: '#2a2a2a',
    color: isHovered ? '#daff00' : '#aaa',
    cursor: images.length >= maxImages ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    opacity: images.length >= maxImages ? 0.5 : 1,
  };

  return (
    <div style={{marginBottom: '24px'}}>
      <label style={labelStyle}>
        Event Images ({images.length}/{maxImages})
      </label>

      {/* Hidden Real Input */}
      <input
        type="file"
        id="file-upload"
        multiple
        accept="image/*"
        onChange={handleFileChange}
        disabled={images.length >= maxImages}
        style={{display: 'none'}}
      />

      {/* Styled Custom Button */}
      <label
        htmlFor="file-upload"
        style={uploadButtonStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}>
        <span style={{fontSize: '2rem', marginBottom: '8px'}}>+</span>
        <span style={{fontWeight: 600}}>
          {images.length >= maxImages ? 'Limit Reached' : 'Add Images'}
        </span>
      </label>

      {/* Preview Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
          gap: '16px',
          marginTop: '20px',
        }}>
        {images.map((img, index) => {
          const previewUrl =
            typeof img === 'string' ? img : URL.createObjectURL(img);

          return (
            <div
              key={index}
              style={{position: 'relative', width: '100%', aspectRatio: '1/1'}}>
              <img
                src={previewUrl}
                alt="preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '12px',
                  border: '2px solid #333',
                }}
              />
              <button
                type="button"
                onClick={() => removeImage(index)}
                style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  background: '#ff4d4d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                }}>
                ✕
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
